/**
 * The reader's grid: what they have marked, and what that forces.
 *
 * A logic grid is drawn as a staircase of blocks, one per pair of categories.
 * A cell lives at (category i, value a) × (category j, value b) with i < j, and
 * holds a tick, a cross, or nothing. This module owns that state and the two
 * questions the help page asks of it — is anything wrong, and is it finished.
 *
 * It knows nothing about hints. src/lib/hint.ts is where the solver comes in.
 */
import type { Puzzle } from "@gen/logic-grid/types.ts";

export type Mark = "tick" | "cross";

/** Keyed `i:a:j:b`, always with i < j so a cell has exactly one name. */
export type Marks = Record<string, Mark>;

export interface Cell {
  /** Row category index. Always less than `colCat`. */
  rowCat: number;
  rowVal: number;
  colCat: number;
  colVal: number;
}

export function cellKey({ rowCat, rowVal, colCat, colVal }: Cell): string {
  return `${rowCat}:${rowVal}:${colCat}:${colVal}`;
}

/** The pair of categories a cell belongs to, as the reader would name it. */
export function blockName(puzzle: Puzzle, cell: Cell): string {
  return `${puzzle.categories[cell.rowCat]?.name} × ${puzzle.categories[cell.colCat]?.name}`;
}

/** How the site names one cell — "Cook Juno × Drink cola". */
export function cellName(puzzle: Puzzle, cell: Cell): string {
  const row = puzzle.categories[cell.rowCat];
  const col = puzzle.categories[cell.colCat];
  return `${row?.name} ${row?.values[cell.rowVal]} × ${col?.name} ${col?.values[cell.colVal]}`;
}

/** Every category pair that gets a block, in the order the grid draws them. */
export function blocks(categoryCount: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i < categoryCount; i++) {
    for (let j = i + 1; j < categoryCount; j++) out.push([i, j]);
  }
  return out;
}

/**
 * Which object slot holds a given value.
 *
 * `solution[cat][slot]` is the value index that slot holds, so finding a value
 * means searching that row. Two cells are true together exactly when they name
 * the same slot.
 */
function slotOf(puzzle: Puzzle, cat: number, val: number): number {
  return puzzle.solution[cat]?.indexOf(val) ?? -1;
}

/** Whether a cell is a tick in the printed answer. */
export function isTrue(puzzle: Puzzle, cell: Cell): boolean {
  return slotOf(puzzle, cell.rowCat, cell.rowVal) === slotOf(puzzle, cell.colCat, cell.colVal);
}

/**
 * Write the active mark into a cell, or rub it out.
 *
 * Only ever the reader's own marks — the crosses a tick implies are not stored,
 * they are worked out by `resolve` when the grid is drawn. That is what makes a
 * mistake fixable: clicking a wrong tick a second time removes it, and every
 * cross it had scattered goes with it, because none of them were ever written
 * down. When they *were* written down there was no way back — clearing the tick
 * left six wrong crosses sitting there to be picked off one at a time.
 *
 * Clicking a cell that already holds the mark you are writing rubs it out. That
 * is the whole undo: the same click that made it.
 */
export function setMark(marks: Marks, cell: Cell, mark: Mark): Marks {
  const next = { ...marks };
  const key = cellKey(cell);
  if (next[key] === mark) delete next[key];
  else next[key] = mark;
  return next;
}

/**
 * The reader's marks plus everything they directly rule out.
 *
 * A tick means the rest of its row and column in that block are crosses — the
 * slashes a solver draws without thinking about them. Derived here rather than
 * stored, so they cannot outlive the tick that implied them, and the reader's
 * own marks always win over a derived one.
 *
 * Every deduction this makes is sound exactly when the tick behind it is, which
 * is why `findMistakes` only has to look at what the reader actually wrote: a
 * derived cross cannot be wrong unless its tick already is.
 */
export function resolve(puzzle: Puzzle, marks: Marks): Marks {
  const out: Marks = {};
  for (const [key, mark] of Object.entries(marks)) {
    if (mark !== "tick") continue;
    const [rowCat, rowVal, colCat, colVal] = key.split(":").map(Number) as [
      number,
      number,
      number,
      number,
    ];
    for (let v = 0; v < puzzle.size; v++) {
      if (v !== colVal) out[cellKey({ rowCat, rowVal, colCat, colVal: v })] = "cross";
      if (v !== rowVal) out[cellKey({ rowCat, rowVal: v, colCat, colVal })] = "cross";
    }
  }
  return { ...out, ...marks };
}

export interface Mistake {
  cell: Cell;
  /** What the reader wrote. The answer is the other one. */
  wrote: Mark;
}

/**
 * Every mark that disagrees with the printed answer.
 *
 * Checked against the solution rather than by running the solver to a
 * contradiction, because the reader needs to be told *where* — and a
 * contradiction surfaces somewhere downstream of the mark that caused it.
 */
export function findMistakes(puzzle: Puzzle, marks: Marks): Mistake[] {
  const out: Mistake[] = [];
  for (const [rowCat, colCat] of blocks(puzzle.categories.length)) {
    for (let rowVal = 0; rowVal < puzzle.size; rowVal++) {
      for (let colVal = 0; colVal < puzzle.size; colVal++) {
        const cell = { rowCat, rowVal, colCat, colVal };
        const wrote = marks[cellKey(cell)];
        if (wrote === undefined) continue;
        if (isTrue(puzzle, cell) !== (wrote === "tick")) out.push({ cell, wrote });
      }
    }
  }
  return out;
}

/** The distinct blocks a set of mistakes falls in, named for the reader. */
export function mistakeBlocks(puzzle: Puzzle, mistakes: Mistake[]): string[] {
  const seen = new Set<string>();
  for (const m of mistakes) seen.add(blockName(puzzle, m.cell));
  return [...seen];
}

/** Whether every true cell carries a tick. Crosses are not required. */
export function isSolved(puzzle: Puzzle, marks: Marks): boolean {
  for (const [rowCat, colCat] of blocks(puzzle.categories.length)) {
    for (let rowVal = 0; rowVal < puzzle.size; rowVal++) {
      for (let colVal = 0; colVal < puzzle.size; colVal++) {
        const cell = { rowCat, rowVal, colCat, colVal };
        if (isTrue(puzzle, cell) && marks[cellKey(cell)] !== "tick") return false;
      }
    }
  }
  return true;
}

/**
 * The reader's marks as text, for reporting a position to someone else.
 *
 * Only what they actually wrote. The crosses a tick implies are left out
 * because `resolve` puts them back, and including them would bury the dozen
 * decisions the reader made under a hundred consequences.
 *
 * Named rather than indexed. `"0:4:1:1"` is the storage format and it means
 * nothing to a person or to anything that does not already have the puzzle in
 * hand, and the whole point of this is being read somewhere else.
 */
export function transcribe(puzzle: Puzzle, marks: Marks, where?: TranscriptSource): string {
  const written: Array<[string, string, Mark]> = [];
  for (const [rowCat, colCat] of blocks(puzzle.categories.length)) {
    const row = puzzle.categories[rowCat];
    const col = puzzle.categories[colCat];
    for (let rowVal = 0; rowVal < puzzle.size; rowVal++) {
      for (let colVal = 0; colVal < puzzle.size; colVal++) {
        const mark = marks[cellKey({ rowCat, rowVal, colCat, colVal })];
        if (!mark) continue;
        written.push([
          `${row?.name} ${row?.values[rowVal]}`,
          `${col?.name} ${col?.values[colVal]}`,
          mark,
        ]);
      }
    }
  }
  return JSON.stringify({ ...where, id: puzzle.id, size: puzzle.size, marks: written }, null, 2);
}

/** Which book and which page these marks came off, when the caller knows. */
export interface TranscriptSource {
  book?: string;
  puzzle?: number;
}

/**
 * Which blocks the reader has written anything in, keyed `i:j` with i < j.
 *
 * Written marks, not resolved ones: a block holding nothing but crosses a tick
 * in another block implied is a block the reader has not been to yet, and
 * showing it as done would misreport how far through they are.
 */
export function blocksWithMarks(puzzle: Puzzle, marks: Marks): Set<string> {
  const out = new Set<string>();
  for (const [rowCat, colCat] of blocks(puzzle.categories.length)) {
    for (let rowVal = 0; rowVal < puzzle.size; rowVal++) {
      for (let colVal = 0; colVal < puzzle.size; colVal++) {
        if (marks[cellKey({ rowCat, rowVal, colCat, colVal })] !== undefined) {
          out.add(`${rowCat}:${colCat}`);
        }
      }
    }
  }
  return out;
}
